import type { FC, ReactNode } from "react";
import { useEffect, useReducer, useRef } from "react";

import classNames from "classnames";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { IconButton, DotsLoader, ItemIcon } from "@similarweb/ui-components";

import { searchTrackerTransportKeywordListId } from "app/actions/searchTrackerActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { useCreateOrUpdateKeywordGroup } from "app/services/keyword-groups/hooks";
import { ScrollAreaWrap } from "pro-features/components/AutocompleteWebCategories/utils";
import type { ICampaignDataUnion, ICampaignsDataUnion } from "app/components/campaigns/shared/CampaignsConst";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { ADD_ICON } from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordConst";
import { TableSelectionKeywordsWarning } from "pro-features/components/TableSelection/src/TableSelectionKeywordsWarning";
import { translate } from "app/filters/i18nFilter";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { emptyKeywordGroup } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SearchTrackerCreationDropdown } from "./SearchTrackerCreationDropdown";
import {
    BRAND_MONITORING_CAMPAIGN_WIZARD_STATE,
    NO_DATA,
    RANK_TRACKER_CAMPAIGN_WIZARD_STATE,
    SearchTrackerFromKeywordsTableReducerTypes,
    getCampaignDataForDropDown,
    initialState,
    reducer,
} from "./SearchTrackerFromKeywordsTableConst";
import {
    AutocompleteStyled,
    CampaignsDropDownItemContent,
    DropDownItem,
    ItemText,
    NoResultsWrapper,
    WarningMessageWrapper,
} from "./SearchTrackerFromKeywordsTableStyle";
import type { ISearchTrackerFromKeywordsTable } from "./SearchTrackerFromKeywordsTableTypes";
import { ICampaignType, WarningTypes } from "./SearchTrackerFromKeywordsTableTypes";
import {
    fetchBrandProtectionCampaignsData,
    fetchRankTrackersCampaignsData,
    fetchSelectedCampaignData,
    getRemainingKeywordsAndCampaignsCount,
    getUniqKeywords,
    getWarningMessage,
    navigateToCampaignPage,
    translateSuccessMessage,
    updateCampaignWithKeywords,
} from "./SearchTrackerFromKeywordsTableUtils";
import { allTrackers } from "app/services/track/track";

export const SearchTrackerFromKeywordsTable: FC<ISearchTrackerFromKeywordsTable> = (props) => {
    const { children, selectedRows } = props;
    const [pageState, dispatch] = useReducer(reducer, initialState);
    const reduxDispatch = useDispatch();
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();
    const {
        shouldDisplayCampaigns,
        campaignType,
        isLoading,
        warningState,
        campaignsRT,
        campaignsBM,
        campaigns,
        userCampaignsDataSettings,
        selectedCampaign,
    } = pageState;

    const isRankTrackerCampaign = campaignType === ICampaignType.RankTracker;
    const autocompleteCampaignsRef = useRef(null);
    const isDisabled = selectedRows?.length === 0;
    const actionButtonsType = isDisabled ? "flat" : "invertedTransparent";

    const updateUserCampaignsDataSettings = async (
        campaignsData: ICampaignsDataUnion,
        cType: ICampaignType,
    ): Promise<void> => {
        const settings = await getRemainingKeywordsAndCampaignsCount(campaignsData, cType);
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_USER_CAMPAIGNS_DATA_SETTINGS,
            payload: settings,
        });
    };

    useEffect(() => {
        updateUserCampaignsDataSettings(campaigns, campaignType);
    }, [campaigns]);

    const onToggle = (isOpen: boolean): void => {
        allTrackers.dispatchTrackEvent("Button", "click", `Search tracker/${isOpen ? "open" : "close"}`);
    };

    const onSearchTrackerClick = (): void => {
        dispatch({ type: SearchTrackerFromKeywordsTable, payload: true });
        onToggle(true);
    };

    const closeCampaign = (): void => {
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_IS_AUTOCOMPLETE_OPEN,
            payload: false,
        });
    };
    const onCancelCampaign = (): void => {
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_IS_AUTOCOMPLETE_OPEN,
            payload: false,
        });
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_WARNING_STATE,
            payload: null,
        });
    };

    const getWarningItems = (message: string): ReactNode => {
        return [
            <TableSelectionKeywordsWarning
                key="SearchTrackerKeywordsWarning"
                onCancel={onCancelCampaign}
                message={message}
            />,
        ];
    };

    const handleCampaignTypeClick = (id: string): void => {
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_CAMPAIGN_TYPE,
            payload:
                (id as ICampaignType) === ICampaignType.RankTracker
                    ? ICampaignType.RankTracker
                    : ICampaignType.BrandProtection,
        });

        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_SHOULD_DISPLAY_CAMPAIGNS,
            payload: true,
        });
    };

    const getCampaignsFromServer = async (): Promise<ICampaignsDataUnion> => {
        let response;

        if (isRankTrackerCampaign) {
            response = await fetchRankTrackersCampaignsData();
        } else {
            response = await fetchBrandProtectionCampaignsData();
        }

        return response;
    };

    const getCampaignsData = async (): Promise<ICampaignsDataUnion> => {
        let campaignsData: ICampaignsDataUnion = [];

        if (!isRankTrackerCampaign && campaignsBM.length > 0) {
            campaignsData = campaignsBM;
        } else if (isRankTrackerCampaign && campaignsRT.length > 0) {
            campaignsData = campaignsRT;
        } else {
            campaignsData = await getCampaignsFromServer();
        }
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_CAMPAIGNS,
            payload: campaignsData,
        });

        return campaignsData;
    };

    const getCampaigns = async (query: string): Promise<ICampaignsDataUnion> => {
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_IS_LOADING,
            payload: true,
        });

        try {
            const campaignsData = await getCampaignsData();
            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_CAMPAIGNS,
                payload: campaignsData,
            });

            if (isRankTrackerCampaign) {
                dispatch({
                    type: SearchTrackerFromKeywordsTableReducerTypes.SET_CAMPAIGNS_RT,
                    payload: campaignsData,
                });
            } else {
                dispatch({
                    type: SearchTrackerFromKeywordsTableReducerTypes.SET_CAMPAIGNS_BP,
                    payload: campaignsData,
                });
            }

            let displayedCampaigns: ICampaignsDataUnion;

            if (!query) {
                displayedCampaigns = campaignsData;
            } else {
                const normalizedQuery = query.trim().toLowerCase();
                displayedCampaigns = campaignsData.filter((campaign) =>
                    campaign.name.trim().toLowerCase().includes(normalizedQuery),
                ) as ICampaignsDataUnion;
                dispatch({
                    type: SearchTrackerFromKeywordsTableReducerTypes.SET_SORTED_CAMPAIGNS,
                    payload: displayedCampaigns,
                });
            }

            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_SHOULD_DISPLAY_CAMPAIGNS,
                payload: true,
            });
            return displayedCampaigns.filter((campaign) => !campaign.isShared) as ICampaignsDataUnion;
        } finally {
            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_IS_LOADING,
                payload: false,
            });
        }
    };

    const handleError = (error): void => {
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_WARNING_STATE,
            payload: WarningTypes.Error,
        });
        Sentry.error(`Error fetch campaign`, error);
    };

    const showSuccessMessage = (onClick: () => void, text: string, linkText: string): void => {
        reduxDispatch(
            showSuccessToast(
                getToastItemComponent({
                    text,
                    linkText,
                    onClick,
                }),
            ),
        );
    };

    const trackCampaignSelection = (item: ICampaignDataUnion): void => {
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Search tracker/${campaignType || ""}/${item.name || ""}/${selectedRows.length.toString() || ""}`,
        );
    };

    const areKeywordsDuplicate = (uniqueKeywords: string[]): boolean => {
        if (uniqueKeywords.length === 0) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `Search tracker/${campaignType || ""}/duplicate keywords error`,
            );

            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_WARNING_STATE,
                payload: WarningTypes.DuplicateKeywords,
            });
            return true;
        }
        return false;
    };

    const areKeywordsLimitExceeded = (item?: ICampaignDataUnion): boolean => {
        const { totalKeywords, usedKeywords } = userCampaignsDataSettings;
        const remainingKeywords = totalKeywords - usedKeywords; // remaining keywords

        const numberOfSelectedWords = item
            ? selectedRows.length * item.scrapingConfigurations.length
            : selectedRows.length;

        if (remainingKeywords < numberOfSelectedWords) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `Search tracker/${campaignType || ""}/keyword limit exceeded `,
            );
            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_WARNING_STATE,
                payload: WarningTypes.KeywordsLimitExceeded,
            });
            return true;
        }
        return false;
    };

    const onCampaignClick = async (item: ICampaignDataUnion): Promise<void> => {
        autocompleteCampaignsRef.current.closePopup();

        try {
            const campaign = await fetchSelectedCampaignData(item, isRankTrackerCampaign);
            const uniqueKeywords = getUniqKeywords(campaign.scrapingConfigurations, selectedRows);
            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_SELECTED_CAMPAIGN,
                payload: campaign,
            });
            if (areKeywordsDuplicate(uniqueKeywords) || areKeywordsLimitExceeded(item)) {
                return;
            }

            const keywordData = campaign.scrapingConfigurations.map((config) => ({
                scrapingGuid: config.id,
                addedKeywords: uniqueKeywords,
            }));
            await updateCampaignWithKeywords(
                { ...item, assetRolesSettings: (campaign as IRTCurrentCampaignData).assetRolesSettings },
                keywordData,
                isRankTrackerCampaign,
            );
            trackCampaignSelection(item);

            showSuccessMessage(
                () => navigateToCampaignPage(item, isRankTrackerCampaign),
                translateSuccessMessage(
                    uniqueKeywords.length * item.scrapingConfigurations.length,
                    item.name,
                    campaignType,
                ),
                translate("table.selection.see.campaign"),
            );
            updateUserCampaignsDataSettings(campaigns, campaignType);
        } catch (error) {
            handleError(error);
        }
    };

    const itemCreatorForCampaign = (item: ICampaignDataUnion, index: number): ReactNode => {
        return (
            <DropDownItem
                key={`item-${index}`}
                id={item.id}
                iconName={null}
                iconSize="sm"
                onClick={() => onCampaignClick(item)}
            >
                <CampaignsDropDownItemContent>
                    <ItemIcon iconSrc={item.asset} />
                    <ItemText>{item.name}</ItemText>
                </CampaignsDropDownItemContent>
            </DropDownItem>
        );
    };

    function generateKeywordListUniqueName(): string {
        const date = new Date();
        const pad = (num: number, size: number) => String(num).padStart(size, "0");

        const uniqueName =
            `list_${date.getFullYear()}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}_` +
            `${pad(date.getHours(), 2)}${pad(date.getMinutes(), 2)}${pad(date.getSeconds(), 2)}`;

        return uniqueName;
    }

    const createKeywordGroupListAndSaveInRedux = async (): Promise<void> => {
        const group = {
            ...emptyKeywordGroup,
            Name: generateKeywordListUniqueName(),
            Keywords: selectedRows,
        };
        const createdGroup = await createOrUpdateKeywordGroup.mutateAsync(group);
        dispatch({ type: SearchTrackerFromKeywordsTableReducerTypes.SET_GROUP, payload: createdGroup });
        reduxDispatch(searchTrackerTransportKeywordListId(createdGroup?.Id));
    };

    const areCampaignsLimitExceeded = (): void => {
        const { totalCampaigns, usedCampaigns } = userCampaignsDataSettings;
        const remainingCampaigns = totalCampaigns - usedCampaigns;
        if (remainingCampaigns === 0) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `Search tracker/${campaignType || ""}/campaign limit exceeded `,
            );
            dispatch({
                type: SearchTrackerFromKeywordsTableReducerTypes.SET_WARNING_STATE,
                payload: WarningTypes.CampaignsLimitExceeded,
            });
            return;
        }
    };

    const onNewCampaignClick = async (): Promise<void> => {
        autocompleteCampaignsRef.current.closePopup();
        if (areKeywordsLimitExceeded() || areCampaignsLimitExceeded()) {
            return;
        }
        await createKeywordGroupListAndSaveInRedux();
        const selectedKeywordsCount = selectedRows.length;
        dispatch({
            type: SearchTrackerFromKeywordsTableReducerTypes.SET_SELECTED_CAMPAIGN,
            payload: {},
        });
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New ${campaignType || ""} campaign/Save/${selectedKeywordsCount.toString() || ""}`,
        );

        const targetState = isRankTrackerCampaign
            ? RANK_TRACKER_CAMPAIGN_WIZARD_STATE
            : BRAND_MONITORING_CAMPAIGN_WIZARD_STATE;

        proNavigator.go(targetState);
    };

    const autocompleteRenderItems = ({ listItems }) => {
        if (!listItems.length && !isLoading) {
            return (
                <NoResultsWrapper>
                    {translate("table.selection.custom.search.tracker.no.results", {
                        campaignType,
                    })}
                </NoResultsWrapper>
            );
        }
        const componentListItems = listItems[0] === NO_DATA ? listItems[1] : listItems.map(itemCreatorForCampaign);

        return <ScrollAreaWrap>{componentListItems}</ScrollAreaWrap>;
    };
    return (
        <>
            {warningState ? (
                <WarningMessageWrapper>
                    {getWarningItems(
                        getWarningMessage(
                            warningState,
                            userCampaignsDataSettings,
                            selectedRows.length,
                            selectedCampaign,
                        ),
                    )}
                </WarningMessageWrapper>
            ) : null}
            {!shouldDisplayCampaigns ? (
                <SearchTrackerCreationDropdown
                    dropdownPopupPlacement={"bottom-left"}
                    isDisabled={isDisabled}
                    campaignDataForDropDown={getCampaignDataForDropDown()}
                    handleCampaignTypeClick={handleCampaignTypeClick}
                    data-automation="search-tracker-description-dropdown"
                >
                    {children}
                </SearchTrackerCreationDropdown>
            ) : null}
            {shouldDisplayCampaigns ? (
                <AutocompleteStyled
                    className="AutocompleteKeywordGroupsAddToList"
                    getListItems={getCampaigns}
                    renderItems={autocompleteRenderItems}
                    loadingComponent={<DotsLoader />}
                    autoFocus={true}
                    floating={true}
                    debounce={250}
                    preventTruncateUnlessForced={true}
                    disabled={isDisabled}
                    actionButtonsType={actionButtonsType}
                    closePopup={closeCampaign}
                    onCancelPopup={onCancelCampaign}
                    placeholder={translate("table.selection.campaign.autocomplete.placeholder")}
                    data-automation="search-tracker-autocomplete-with-add-button"
                    shouldAddNewItemToList={true}
                    onAddNewItemToListClick={onNewCampaignClick}
                    addNewItemToListText={translate(
                        isRankTrackerCampaign
                            ? "table.selection.create.rank.tracker.campaign"
                            : "table.selection.create.brand.protection.campaign",
                    )}
                    addPopUpOption={true}
                    popupConfig={{
                        height: 360,
                        width: 360,
                        placement: "bottom-left",
                        cssClassContainer: classNames(
                            "DropdownContent-container",
                            "Popup-content-for-group-list-dropdown",
                            "Popup-content--pro-dropdown",
                        ),
                        defaultOpen: true,
                        enabled: !isDisabled,
                        onToggle: (isOpen) =>
                            !isOpen &&
                            dispatch({
                                type: SearchTrackerFromKeywordsTableReducerTypes.SET_SHOULD_DISPLAY_CAMPAIGNS,
                                payload: false,
                            }),
                    }}
                    popupButton={
                        <IconButton
                            type={actionButtonsType}
                            iconName={ADD_ICON}
                            iconSize="xs"
                            onClick={onSearchTrackerClick}
                            isDisabled={isDisabled}
                        >
                            {translate("table.selection.search.tracker")}
                        </IconButton>
                    }
                    popupRef={(ref) => (autocompleteCampaignsRef.current = ref)}
                />
            ) : null}
        </>
    );
};
